// Adapted from T3 Code packages/client-runtime SSH gateway; MIT, docs/licenses/T3-Code-MIT.txt.

/**
 * Reaching an Environment over the user's own SSH, from the client runtime's point of view.
 *
 * The runtime never opens a forward. It asks whichever surface it is running on to, and only the
 * Desktop Client can: a forward is a process on the machine the client runs on. The default
 * refuses, so a browser surface never appears able to reach an SSH Environment.
 */
import type { EnvironmentId, SshEnvironmentTarget } from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";

import { ConnectionBlockedError, type ConnectionAttemptError } from "./model.ts";

/**
 * What one prepared SSH connection is.
 *
 * The URLs are loopback addresses that exist only while the forward does, and `session` is the
 * Environment Access the destination itself issued — reaching the machine was never authorization
 * on its own.
 */
export interface PreparedSshEnvironment {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  /** The target as `ssh -G` resolved it, which may differ from the one that was stored. */
  readonly target: SshEnvironmentTarget;
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly session: string;
}

export interface SshEnvironmentGatewayService {
  /**
   * First contact: open a forward, learn which Environment answered, and redeem one grant.
   * Used when adding a connection, where the Environment's identity is what is being discovered.
   */
  readonly provision: (
    target: SshEnvironmentTarget,
  ) => Effect.Effect<PreparedSshEnvironment, ConnectionAttemptError>;
  /** Every connection after the first, where the Environment's identity is already known. */
  readonly prepare: (input: {
    readonly expectedEnvironmentId: EnvironmentId;
    readonly target: SshEnvironmentTarget;
  }) => Effect.Effect<PreparedSshEnvironment, ConnectionAttemptError>;
  readonly disconnect: (
    target: SshEnvironmentTarget,
  ) => Effect.Effect<void, ConnectionAttemptError>;
}

const unsupported = (detail: string) =>
  Effect.fail(new ConnectionBlockedError({ reason: "unsupported", detail }));

const WITHOUT_FORWARD =
  "An SSH Environment is reached through a forward only the desktop app can open.";

/**
 * Supplied by a surface that can open a forward. The default refuses rather than failing later,
 * so a browser client shows "not available here" instead of a connection that never arrives.
 */
export class SshEnvironmentGateway extends Context.Reference<SshEnvironmentGatewayService>(
  "@afar-research/client-runtime/connection/SshEnvironmentGateway",
  {
    defaultValue: (): SshEnvironmentGatewayService => ({
      provision: () => unsupported(WITHOUT_FORWARD),
      prepare: () => unsupported(WITHOUT_FORWARD),
      // Disconnecting something that was never connected is not a failure worth reporting.
      disconnect: () => Effect.void,
    }),
  },
) {}
